import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  Expense,
  ExpenseInput,
  ExpenseTemplate,
  ExpenseTemplateCreate,
  ExpenseTemplateUpdate,
  ExpenseTotals,
} from '@vuokramani/shared';
import { api } from './api.ts';

/** Kulut näkyvät myös huoltokirjassa (linkki) ja lokissa; kuitit tiedostoissa. */
function invalidate(queryClient: QueryClient, propertyId: string) {
  for (const key of [
    ['expenses', propertyId],
    ['expenseTemplates', propertyId],
    ['events', propertyId],
    ['maintenance', propertyId],
    ['files', propertyId],
  ]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

export function useExpenses(propertyId: string | undefined, year: number) {
  return useQuery({
    queryKey: ['expenses', propertyId, year],
    queryFn: () =>
      api<{ expenses: Expense[]; totals: ExpenseTotals }>(
        `/properties/${propertyId}/expenses?year=${year}`,
      ),
    enabled: Boolean(propertyId),
  });
}

export function useSaveExpense(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: ExpenseInput }) =>
      id
        ? api<Expense>(`/expenses/${id}`, { method: 'PUT', body })
        : api<Expense>(`/properties/${propertyId}/expenses`, { method: 'POST', body }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useDeleteExpense(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/expenses/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useExpenseTemplates(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['expenseTemplates', propertyId],
    queryFn: () =>
      api<{ templates: ExpenseTemplate[] }>(`/properties/${propertyId}/expense-templates`).then(
        (r) => r.templates,
      ),
    enabled: Boolean(propertyId),
  });
}

export function useCreateExpenseTemplate(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ExpenseTemplateCreate) =>
      api<ExpenseTemplate>(`/properties/${propertyId}/expense-templates`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useUpdateExpenseTemplate(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ExpenseTemplateUpdate }) =>
      api<ExpenseTemplate>(`/expense-templates/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useDeleteExpenseTemplate(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/expense-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}
